import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
    Link as RouterLink,
    Navigate,
    useNavigate,
    useParams,
} from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import {
    LocalShippingOutlined as DeliveryIcon,
    LockOutlined as LockIcon,
    Storefront as PickupIcon,
} from '@mui/icons-material';

import {
    fetchCart,
    checkoutCart,
    quoteCartShipping,
} from '../../actions/cartActions';
import { formatPrice } from '../../lib/money';
import { paymentOptionText } from '../../lib/payments';
import {
    EMPTY_ADDRESS,
    isAddressComplete,
    normalizePostalCode,
} from '../../lib/address';
import {
    COLORS,
    FONT_BODY,
    FONT_TITLE,
    callButtonSx,
} from '../storefront/storefrontStyles';
import CartLayout, { darkAlertSx, panelSx } from './CartLayout';
import LeaveForMercadoPagoDialog from '../payment/LeaveForMercadoPagoDialog';
import PaywayPaymentForm from '../payment/PaywayPaymentForm';
import { getPaywayAccessToken } from '../../lib/payments';
import { CartLine, CartStoreHeader } from './CartPage';
import { cartPath } from './cartPaths';
import HomeDeliveryForm from './HomeDeliveryForm';

const NO_OPTIONS = [];
const PICKUP = 1;
const HOME_DELIVERY = 3;
// el CP se puede tipear: se espera a que deje de escribir antes de cotizar
const QUOTE_DEBOUNCE_MS = 500;

const round2 = (n) => Math.round(n * 100) / 100;

const radioSx = {
    color: COLORS.muted,
    '&.Mui-checked': { color: COLORS.call },
};

const SectionTitle = ({ children }) => (
    <Typography
        sx={{
            fontFamily: FONT_TITLE,
            fontWeight: 700,
            fontSize: '1.05rem',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            mb: 1,
        }}
    >
        {children}
    </Typography>
);

const deliveryOptionSx = {
    alignItems: 'flex-start',
    mr: 0,
    py: 0.5,
    '& .MuiRadio-root': { mt: -0.5 },
    '& .MuiFormControlLabel-label': { flexGrow: 1 },
    '& .MuiFormControlLabel-label.Mui-disabled': { color: COLORS.subtle },
};

const DeliveryLabel = ({ icon: Icon, title, detail, price }) => (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Icon sx={{ color: COLORS.call, mt: 0.25 }} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
            {detail && (
                <Typography sx={{ color: COLORS.muted }}>{detail}</Typography>
            )}
        </Box>
        {price && (
            <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {price}
            </Typography>
        )}
    </Box>
);

// Marco de la página: el de /checkout, o un contenedor pelado dentro del
// diálogo de la llamada. A nivel de módulo a propósito: definido dentro del
// componente cambiaría de identidad en cada render y remontaría el formulario
// (los campos de la dirección perderían el foco al tipear).
const Frame = ({ inCall, storeId, title, children }) =>
    inCall ? (
        <Box
            sx={{
                backgroundColor: COLORS.background,
                color: COLORS.text,
                fontFamily: FONT_BODY,
                minHeight: '100%',
                px: { xs: 1, sm: 2 },
                py: 2,
            }}
        >
            {children}
        </Box>
    ) : (
        <CartLayout
            backTo={cartPath(storeId)}
            backLabel="Volver al carrito"
            title={title}
        >
            {children}
        </CartLayout>
    );

// `inCall`: montado en un diálogo sobre /call (cart/InCallCheckoutDialog). Ahí
// no se navega nunca —desmontaría la llamada—: los "volver al carrito" se
// vuelven avisos, el pedido se vincula a la llamada (`callRequestId`) y el
// pago se resuelve en el mismo diálogo: Payway con el formulario de tarjeta,
// MercadoPago en otra pestaña tras avisar que la llamada sigue en ésta.
// `live`: lo mismo sobre el vivo (LiveViewer), sólo cambian los textos.
const CheckoutPage = ({
    storeId,
    storeCart,
    customer,
    fetchCart,
    checkoutCart,
    inCall = false,
    live = false,
    callRequestId,
    onClose,
    onPaid,
}) => {
    const navigate = useNavigate();
    // En la llamada: el pedido ya creado, esperando el pago en este diálogo.
    const [payResult, setPayResult] = useState(null);
    const [leaveOpen, setLeaveOpen] = useState(false);
    const [ready, setReady] = useState(false);
    const [paymentOptionId, setPaymentOptionId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [shippingOptionId, setShippingOptionId] = useState(PICKUP);
    const [address, setAddress] = useState(EMPTY_ADDRESS);
    // { postalCode, price, ... } de la última cotización buena
    const [quote, setQuote] = useState(null);
    const [quoting, setQuoting] = useState(false);
    const [quoteError, setQuoteError] = useState(null);
    // aviso del 409 cart_total_changed (el total o el envío cambió)
    const [changedNotice, setChangedNotice] = useState(null);
    const [addressError, setAddressError] = useState(null);
    const [quoteAttempt, setQuoteAttempt] = useState(0);
    const quoteSeq = useRef(0);
    // el estado de React no alcanza contra un doble click en el mismo tick
    const submittingRef = useRef(false);

    // Siempre releemos: lo que se muestra acá es lo que se manda como
    // expectedTotal.
    useEffect(() => {
        let cancelled = false;
        fetchCart(storeId).then(() => {
            if (!cancelled) setReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, [storeId, fetchCart]);

    const cart = storeCart && storeCart.cart;
    const paymentOptions = (cart && cart.checkout.paymentOptions) || NO_OPTIONS;

    // Con un único medio de pago, preseleccionado. Si el elegido deja de
    // estar disponible tras una relectura, se descarta.
    useEffect(() => {
        if (paymentOptions.length === 1) {
            setPaymentOptionId(paymentOptions[0].paymentOptionId);
        } else if (
            paymentOptionId !== null &&
            !paymentOptions.some((o) => o.paymentOptionId === paymentOptionId)
        ) {
            setPaymentOptionId(null);
        }
    }, [paymentOptions, paymentOptionId]);

    const homeDeliveryOffered = Boolean(
        cart &&
            cart.checkout.homeDelivery &&
            cart.checkout.homeDelivery.available &&
            cart.checkout.shippingOptions.some(
                (o) => o.shippingOptionId === HOME_DELIVERY
            )
    );
    const homeDelivery =
        homeDeliveryOffered && shippingOptionId === HOME_DELIVERY;

    // Si tras una relectura el envío deja de ofrecerse, queda el retiro.
    useEffect(() => {
        if (!homeDeliveryOffered && shippingOptionId !== PICKUP) {
            setShippingOptionId(PICKUP);
        }
    }, [homeDeliveryOffered, shippingOptionId]);

    // Cotiza cuando hay un CP válido distinto del ya cotizado. Una respuesta
    // vieja (CP cambiado mientras tanto) se descarta.
    const postalCode = normalizePostalCode(address.postalCode);
    const quotedPostalCode = quote && quote.postalCode;
    useEffect(() => {
        if (!homeDelivery || !postalCode) {
            quoteSeq.current += 1;
            setQuoting(false);
            setQuoteError(null);
            return undefined;
        }
        if (postalCode === quotedPostalCode) {
            return undefined;
        }

        const seq = ++quoteSeq.current;
        setQuoting(true);
        setQuoteError(null);
        const timer = setTimeout(async () => {
            try {
                const result = await quoteCartShipping(storeId, postalCode);
                if (seq !== quoteSeq.current) return;
                setQuote(result);
            } catch (err) {
                if (seq !== quoteSeq.current) return;
                setQuote(null);
                setQuoteError(err.message);
            } finally {
                if (seq === quoteSeq.current) setQuoting(false);
            }
        }, QUOTE_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [homeDelivery, postalCode, quotedPostalCode, storeId, quoteAttempt]);

    const backTo = live ? 'al vivo' : 'a la llamada';

    const inCallNotice = (text) => (
        <Frame inCall={inCall} storeId={storeId} title="Finalizar compra">
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography sx={{ mb: 3 }}>{text}</Typography>
                <Button variant="contained" onClick={onClose}>
                    Volver {backTo}
                </Button>
            </Box>
        </Frame>
    );

    if (!ready || !cart) {
        if (ready && storeCart && storeCart.failed) {
            return inCall
                ? inCallNotice('No pudimos cargar tu carrito. Probá de nuevo en un momento.')
                : <Navigate to={cartPath(storeId)} replace />;
        }
        return (
            <Frame inCall={inCall} storeId={storeId} title="Finalizar compra">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: COLORS.accent }} />
                </Box>
            </Frame>
        );
    }

    // En la llamada, con el pedido ya creado: Payway se paga acá mismo.
    if (payResult && payResult.paymentProvider === 'payway') {
        return (
            <Frame inCall={inCall} storeId={storeId} title="Pagar con tarjeta">
                <PaywayPaymentForm
                    purchaseOrderId={payResult.purchaseOrderId}
                    accessToken={getPaywayAccessToken(payResult)}
                    onApproved={() => {
                        if (onPaid) onPaid(payResult.purchaseOrderId);
                    }}
                    approvedAction={
                        <Button variant="contained" onClick={onClose}>
                            Volver {backTo}
                        </Button>
                    }
                />
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button variant="text" onClick={onClose} sx={{ color: COLORS.muted }}>
                        Pagar más tarde y volver {backTo}
                    </Button>
                </Box>
            </Frame>
        );
    }

    // Vacío, con productos no disponibles o sin medios de pago: el carrito
    // explica por qué no se puede seguir. (Tras un pago exitoso no se llega
    // acá: el botón queda en "Redirigiendo…" mientras cambia la página.)
    if (!cart.checkout.canCheckout && !submitting && !payResult) {
        return inCall
            ? inCallNotice(
                  (changedNotice && `${changedNotice} `) ||
                      'Tu carrito no se puede comprar ahora. Revisalo desde el carrito y volvé a intentar.'
              )
            : <Navigate to={cartPath(storeId)} replace />;
    }

    const pickupOption =
        cart.checkout.shippingOptions.find(
            (o) => o.shippingOptionId === PICKUP
        ) || cart.checkout.shippingOptions[0];
    const storeAddress = cart.store && cart.store.address;
    const quoteReady =
        homeDelivery &&
        !quoting &&
        Boolean(quote) &&
        quote.postalCode === postalCode;
    const shippingPrice = homeDelivery && quoteReady ? quote.price : 0;
    const total = round2(cart.subtotal + shippingPrice);
    const deliveryReady =
        !homeDelivery || (quoteReady && isAddressComplete(address));
    const buyerName = customer
        ? [customer.name, customer.lastName].filter(Boolean).join(' ')
        : '';
    const buyerEmail = customer && customer.email;
    const availableItems = cart.items.filter((item) => item.available);

    const handlePay = async () => {
        if (submittingRef.current || !paymentOptionId || !deliveryReady) {
            return;
        }
        submittingRef.current = true;
        setSubmitting(true);
        setEmailError(null);
        setAddressError(null);
        setChangedNotice(null);

        const outcome = await checkoutCart(storeId, {
            paymentOptionId,
            shippingOptionId: homeDelivery ? HOME_DELIVERY : PICKUP,
            address: homeDelivery
                ? {
                      streetName: address.streetName.trim(),
                      streetNumber: address.streetNumber.trim(),
                      floor: address.floor.trim() || null,
                      apartment: address.apartment.trim() || null,
                      city: address.city.trim(),
                      province: address.province,
                      postalCode,
                  }
                : undefined,
            expectedTotal: total,
            callRequestId: inCall ? callRequestId : undefined,
        });

        if (outcome.ok) {
            if (inCall) {
                // El pedido existe y el carrito se cerró. Nada de navegar: el
                // pago sigue en este diálogo (Payway) o en otra pestaña (MP).
                const result = outcome.result;
                submittingRef.current = false;
                setSubmitting(false);
                if (result.paymentProvider === 'payway' && getPaywayAccessToken(result)) {
                    setPayResult(result);
                } else if (result.paymentProvider === 'mercadopago' && result.paymentLink) {
                    setPayResult(result);
                    setLeaveOpen(true);
                } else {
                    if (result.paymentLink) {
                        window.open(result.paymentLink, '_blank', 'noopener,noreferrer');
                    }
                    if (onClose) onClose();
                }
                return;
            }
            // MercadoPago: init_point externo; Payway: /pagar/:id?t= propio.
            // Se queda "Redirigiendo…" hasta que el navegador cambia de página.
            window.location.assign(outcome.result.paymentLink);
            return;
        }

        submittingRef.current = false;
        setSubmitting(false);

        if (outcome.conflict && outcome.code === 'cart_total_changed') {
            // El carrito ya se refrescó en redux; la cotización nueva viene
            // con él. Se muestra el total nuevo y el comprador vuelve a
            // tocar "Pagar".
            if (outcome.shippingQuote) setQuote(outcome.shippingQuote);
            setChangedNotice(outcome.message);
        } else if (outcome.conflict) {
            // En la llamada no se navega: el carrito fresco ya está en redux y
            // la página se vuelve a armar con él (o avisa si quedó incomprable).
            if (inCall) setChangedNotice(outcome.message);
            else navigate(cartPath(storeId), { replace: true });
        } else if (outcome.code === 'customer_email_required') {
            setEmailError(outcome.message);
        } else if (outcome.code === 'invalid_address') {
            setAddressError(outcome.message);
        }
    };

    const leaveDialog = inCall && payResult ? (
        <LeaveForMercadoPagoDialog
            open={leaveOpen}
            storeName={cart.store && cart.store.name}
            live={live}
            onClose={() => {
                setLeaveOpen(false);
                if (onClose) onClose();
            }}
            onConfirm={() => {
                window.open(payResult.paymentLink, '_blank', 'noopener,noreferrer');
                setLeaveOpen(false);
                if (onClose) onClose();
            }}
        />
    ) : null;

    return (
        <Frame inCall={inCall} storeId={storeId} title="Finalizar compra">
            {leaveDialog}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={panelSx}>
                    <CartStoreHeader store={cart.store} />
                    <Divider sx={{ borderColor: COLORS.border }} />
                    {availableItems.map((item) => (
                        <Box key={item.cartItemId}>
                            <CartLine item={item} readOnly />
                            <Divider sx={{ borderColor: COLORS.border }} />
                        </Box>
                    ))}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 2,
                            color: COLORS.muted,
                        }}
                    >
                        <Typography>
                            {homeDelivery
                                ? 'Envío · Correo Argentino Expreso'
                                : 'Envío'}
                        </Typography>
                        <Typography>
                            {!homeDelivery
                                ? 'Gratis'
                                : quoteReady
                                ? formatPrice(quote.price)
                                : 'A cotizar'}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            mt: 1,
                        }}
                    >
                        <Typography sx={{ fontWeight: 600 }}>Total</Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_BODY,
                                fontWeight: 700,
                                fontSize: '1.4rem',
                            }}
                        >
                            {formatPrice(total)}
                        </Typography>
                    </Box>
                </Box>

                {changedNotice && (
                    <Alert
                        severity="warning"
                        sx={darkAlertSx}
                        onClose={() => setChangedNotice(null)}
                    >
                        {changedNotice}
                    </Alert>
                )}

                <Box sx={panelSx}>
                    <SectionTitle>Comprador</SectionTitle>
                    <Typography>{buyerName || 'Sin nombre'}</Typography>
                    <Typography sx={{ color: COLORS.muted }}>
                        {buyerEmail || 'Tu cuenta no tiene un email.'}
                    </Typography>
                    {(emailError || !buyerEmail) && (
                        <Alert
                            severity="warning"
                            sx={{ ...darkAlertSx, mt: 1.5 }}
                            action={
                                <Button
                                    component={RouterLink}
                                    to="/account"
                                    size="small"
                                    sx={{ color: COLORS.accent }}
                                >
                                    Mi cuenta
                                </Button>
                            }
                        >
                            {emailError ||
                                'Necesitamos un email para mandarte la confirmación de la compra.'}
                        </Alert>
                    )}
                </Box>

                <Box sx={panelSx}>
                    <SectionTitle>Entrega</SectionTitle>
                    {homeDeliveryOffered ? (
                        <RadioGroup
                            value={String(
                                homeDelivery ? HOME_DELIVERY : PICKUP
                            )}
                            onChange={(e) => {
                                setShippingOptionId(Number(e.target.value));
                                setChangedNotice(null);
                            }}
                        >
                            <FormControlLabel
                                value={String(PICKUP)}
                                disabled={submitting}
                                control={<Radio sx={radioSx} />}
                                label={
                                    <DeliveryLabel
                                        icon={PickupIcon}
                                        title="Retiro en la tienda"
                                        detail={
                                            storeAddress ||
                                            (pickupOption &&
                                                pickupOption.description) ||
                                            'Coordiná el retiro con la tienda.'
                                        }
                                        price="Gratis"
                                    />
                                }
                                sx={deliveryOptionSx}
                            />
                            <FormControlLabel
                                value={String(HOME_DELIVERY)}
                                disabled={submitting}
                                control={<Radio sx={radioSx} />}
                                label={
                                    <DeliveryLabel
                                        icon={DeliveryIcon}
                                        title="Envío a domicilio · Correo Argentino"
                                        detail="Expreso a domicilio"
                                        price={
                                            quoteReady
                                                ? formatPrice(quote.price)
                                                : null
                                        }
                                    />
                                }
                                sx={deliveryOptionSx}
                            />
                        </RadioGroup>
                    ) : (
                        <DeliveryLabel
                            icon={PickupIcon}
                            title="Retiro en la tienda"
                            detail={
                                storeAddress ||
                                (pickupOption && pickupOption.description) ||
                                'Coordiná el retiro con la tienda.'
                            }
                        />
                    )}

                    {homeDelivery && (
                        <Box sx={{ mt: 2 }}>
                            <HomeDeliveryForm
                                address={address}
                                onChange={(next) => {
                                    setAddress(next);
                                    setAddressError(null);
                                }}
                                disabled={submitting}
                            />
                            <Box sx={{ mt: 1.5 }}>
                                {quoting ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            color: COLORS.muted,
                                        }}
                                    >
                                        <CircularProgress
                                            size={16}
                                            sx={{ color: COLORS.muted }}
                                        />
                                        <Typography>
                                            Cotizando el envío…
                                        </Typography>
                                    </Box>
                                ) : quoteError ? (
                                    <Alert
                                        severity="warning"
                                        sx={darkAlertSx}
                                        action={
                                            <Button
                                                size="small"
                                                onClick={() =>
                                                    setQuoteAttempt(
                                                        (n) => n + 1
                                                    )
                                                }
                                                sx={{ color: COLORS.accent }}
                                            >
                                                Reintentar
                                            </Button>
                                        }
                                    >
                                        {quoteError}
                                    </Alert>
                                ) : quoteReady ? (
                                    <Typography sx={{ fontWeight: 600 }}>
                                        Envío: {formatPrice(quote.price)} ·
                                        Correo Argentino Expreso
                                    </Typography>
                                ) : null}
                                {addressError && (
                                    <Alert
                                        severity="warning"
                                        sx={{ ...darkAlertSx, mt: 1 }}
                                    >
                                        {addressError}
                                    </Alert>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box sx={panelSx}>
                    <SectionTitle>Medio de pago</SectionTitle>
                    <RadioGroup
                        value={
                            paymentOptionId === null
                                ? ''
                                : String(paymentOptionId)
                        }
                        onChange={(e) =>
                            setPaymentOptionId(Number(e.target.value))
                        }
                    >
                        {paymentOptions.map((option) => (
                            <FormControlLabel
                                key={option.paymentOptionId}
                                value={String(option.paymentOptionId)}
                                disabled={submitting}
                                control={<Radio sx={radioSx} />}
                                label={
                                    <Box>
                                        <Typography sx={{ fontWeight: 600 }}>
                                            {paymentOptionText(option).title}
                                        </Typography>
                                        {paymentOptionText(option).subtitle && (
                                            <Typography
                                                sx={{
                                                    color: COLORS.muted,
                                                    fontSize: '0.85rem',
                                                }}
                                            >
                                                {paymentOptionText(option).subtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                sx={{
                                    py: 0.5,
                                    '& .MuiFormControlLabel-label.Mui-disabled': {
                                        color: COLORS.subtle,
                                    },
                                }}
                            />
                        ))}
                    </RadioGroup>
                </Box>

                <Button
                    variant="contained"
                    size="large"
                    startIcon={
                        submitting ? (
                            <CircularProgress
                                size={18}
                                sx={{ color: COLORS.background }}
                            />
                        ) : (
                            <LockIcon />
                        )
                    }
                    disabled={submitting || !paymentOptionId || !deliveryReady}
                    onClick={handlePay}
                    sx={{
                        ...callButtonSx,
                        alignSelf: { xs: 'stretch', sm: 'flex-end' },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(255,255,255,0.12)',
                            color: 'rgba(255,255,255,0.4)',
                        },
                    }}
                >
                    {submitting
                        ? 'Redirigiendo al pago…'
                        : `Pagar ${formatPrice(total)}`}
                </Button>
                {(homeDelivery && !deliveryReady) ||
                (!paymentOptionId && paymentOptions.length > 1) ? (
                    <Typography
                        sx={{
                            color: COLORS.subtle,
                            fontSize: '0.8rem',
                            textAlign: { xs: 'center', sm: 'right' },
                        }}
                    >
                        {homeDelivery && !deliveryReady
                            ? 'Completá la dirección de entrega para cotizar el envío.'
                            : 'Elegí un medio de pago para continuar.'}
                    </Typography>
                ) : null}
            </Box>
        </Frame>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        storeCart: state.cart.byStore[ownProps.storeId],
        customer: state.customer.customer,
    };
};

export const ConnectedCheckoutPage = connect(mapStateToProps, {
    fetchCart,
    checkoutCart,
})(CheckoutPage);

const CheckoutPageWrapper = () => {
    const { storeId } = useParams();
    return <ConnectedCheckoutPage key={storeId} storeId={storeId} />;
};

export default CheckoutPageWrapper;
